import { ChatLayoutContent } from '@/components/chat/ChatLayoutContent';
import {
  CORPUS_QA,
  DATAVOYAGER,
  MULTI_TOOL,
  PAPER_FINDER,
  TABLE_ANNOTATION,
} from 'configs/NoraVariants';
import {
  CORPUS_QA_SUGGESTIONS,
  DATAVOYAGER_SUGGESTIONS,
  MULTI_TOOL_SUGGESTIONS,
  PAPER_FINDER_SUGGESTIONS,
  TABLE_ANNOTATION_SUGGESTIONS,
} from '@/components/chat/constants/SuggestedQueries';
import { CrossLinks } from '@/components/chat/CrossLinks';
import { optAppConfig } from '@/utils/config';
import IconAstaLogo from '@/components/icons/IconAstaLogo';
import IconDataVoyagerLogo from '@/components/icons/IconDataVoyagerLogo';
import IconPaperFinderLogo from '@/components/icons/IconPaperFinderLogo';
import IconScholarQALogo from '@/components/icons/IconScholarQALogo';

import { Box, Link, Typography } from '@mui/material';
import { Fragment, MouseEvent } from 'react';
import styled from 'styled-components';

export const Suggestions = ({
  handleShowSuggestion,
  handleSendMessage,
  onFilesChanged,
  isMessageBarPending,
}: {
  handleShowSuggestion: (
    event: MouseEvent<HTMLAnchorElement>,
    suggestion: string,
  ) => void;
  handleSendMessage: (text: string) => Promise<void>;
  onFilesChanged?: (files: File[]) => void;
  isMessageBarPending: boolean;
}) => {
  const noraExperienceVariant = optAppConfig((_) => _.experience.variant);
  // render different suggestions depending on if we are in the multi-tool or corpus-qa experience
  const getSuggestions = () => {
    switch (noraExperienceVariant) {
      case MULTI_TOOL: {
        return MULTI_TOOL_SUGGESTIONS;
      }
      case CORPUS_QA: {
        return CORPUS_QA_SUGGESTIONS;
      }
      case TABLE_ANNOTATION: {
        return TABLE_ANNOTATION_SUGGESTIONS;
      }
      case PAPER_FINDER: {
        return PAPER_FINDER_SUGGESTIONS;
      }
      case DATAVOYAGER: {
        return DATAVOYAGER_SUGGESTIONS;
      }
      default: {
        return MULTI_TOOL_SUGGESTIONS;
      }
    }
  };
  const suggestions = getSuggestions();

  // render different logos
  const toolLogo =
    noraExperienceVariant === CORPUS_QA ? (
      <IconScholarQALogo />
    ) : noraExperienceVariant === PAPER_FINDER ? (
      <IconPaperFinderLogo />
    ) : noraExperienceVariant === DATAVOYAGER ? (
      <IconDataVoyagerLogo />
    ) : (
      <IconAstaLogo />
    );

  // render different taglines depending one experience
  const getTagLine = () => {
    const noraExperienceVariant = optAppConfig((_) => _.experience.variant);

    switch (noraExperienceVariant) {
      case CORPUS_QA: {
        return (
          <Typography
            variant="body1"
            sx={{ mb: '24px', color: 'rgba(250, 242, 233, 1)' }}
          >
            A research tool for literature synthesis, with broad and deep
            coverage via a corpus of 8M+ full text papers and 108M+ abstracts.
            Ai2 Scholar QA can make mistakes. Check source documents by
            following citations. A project from{' '}
            <Link
              href="https://allenai.org/"
              target="_blank"
              sx={{ color: 'rgba(15, 203, 140, 1)' }}
            >
              Ai2
            </Link>
            .
          </Typography>
        );
      }
      case PAPER_FINDER: {
        return (
          <Typography
            variant="body1"
            sx={{ mb: '24px', color: 'rgba(250, 242, 233, 1)' }}
          >
            A research tool for paper discovery, with broad and deep coverage
            via a corpus of 8M+ full text papers and 108M+ abstracts. A project
            from{' '}
            <Link
              href="https://allenai.org/"
              target="_blank"
              sx={{ color: 'rgba(15, 203, 140, 1)' }}
            >
              Ai2
            </Link>
            .
          </Typography>
        );
      }
      case DATAVOYAGER: {
        return (
          <Typography
            variant="body1"
            sx={{ mb: '24px', color: 'rgba(250, 242, 233, 1)' }}
          >
            A research tool for data analysis and discovery, enabling natural
            language exploration and reproducible data-driven research on
            user-uploaded datasets. Ai2 DataVoyager can make mistakes. Review
            generated code and outputs for correctness. A project from{' '}
            <Link
              href="https://allenai.org/"
              target="_blank"
              sx={{ color: 'rgba(15, 203, 140, 1)' }}
            >
              Ai2
            </Link>
            .
          </Typography>
        );
      }
      default: {
        return null;
      }
    }
  };
  const tagLine = getTagLine();

  const shouldShowCrossLinks =
    noraExperienceVariant === PAPER_FINDER ||
    noraExperienceVariant === CORPUS_QA ||
    noraExperienceVariant === DATAVOYAGER;

  return (
    <WrapperBox $shouldShowCrossLinks={shouldShowCrossLinks}>
      {shouldShowCrossLinks ? (
        <Fragment>
          <StyledBoxChatLayoutWrapper>
            <ChatLayoutContent
              toolLogo={toolLogo}
              tagLine={tagLine}
              handleSendMessage={handleSendMessage}
              onFilesChanged={onFilesChanged}
              handleShowSuggestion={handleShowSuggestion}
              isMessageBarPending={isMessageBarPending}
              suggestions={suggestions}
            />
          </StyledBoxChatLayoutWrapper>
          <StyledBox>
            <CrossLinks />
          </StyledBox>
        </Fragment>
      ) : (
        <ChatLayoutContent
          toolLogo={toolLogo}
          tagLine={tagLine}
          handleSendMessage={handleSendMessage}
          onFilesChanged={onFilesChanged}
          handleShowSuggestion={handleShowSuggestion}
          isMessageBarPending={isMessageBarPending}
          suggestions={suggestions}
        />
      )}
    </WrapperBox>
  );
};

const WrapperBox = styled(Box)<{ $shouldShowCrossLinks: boolean }>`
  color: #ffffff;
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: center;
  width: 100%;
  padding: ${({ $shouldShowCrossLinks }) =>
    $shouldShowCrossLinks ? '12px' : '0'};

  @media (min-width: 600px) {
    padding: ${({ $shouldShowCrossLinks }) =>
      $shouldShowCrossLinks ? '24px 0' : '0'};
  }
`;

const StyledBox = styled(Box)`
  margin-top: auto;
`;

const StyledBoxChatLayoutWrapper = styled(Box)`
  display: flex;
  flex-direction: column;
  justify-content: center;
  flex: 1;

  @media (max-width: ${({ theme }) => theme.breakpoints.values.sm}px) {
    flex: 0;
  }
`;
